import { useEffect, useState, useCallback } from 'react';
import {
    Container, Typography, Card, CardContent, Stack, Box, Grid, Button,
    TextField, Switch, FormControlLabel, Divider, CircularProgress, Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import saveFill from '@iconify/icons-eva/save-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import Page from 'src/components/Page';
import { adminSyndicationApi } from 'src/utils/syndicationApi';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 1 }}>{children}</Typography>
);

export default function AdminSettings() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await adminSyndicationApi.get('admin/config/');
            setConfig(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load configuration.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setConfig((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await adminSyndicationApi.patch('admin/config/', config);
            setConfig(res.data.data);
            showToast('Configuration saved successfully.');
        } catch (err) {
            showErrorToast(err.response?.data?.message || 'Failed to save configuration.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Page title="Syndication Settings">
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Box display="flex" justifyContent="center" py={10}>
                        <CircularProgress />
                    </Box>
                </Container>
            </Page>
        );
    }

    if (error) {
        return (
            <Page title="Syndication Settings">
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Container>
            </Page>
        );
    }

    return (
        <Page title="Syndication Settings">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h4" fontWeight={700}>Syndication Settings</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<Icon icon={refreshFill} />}
                            onClick={fetchConfig}
                        >
                            Reload
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Icon icon={saveFill} />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Stack>
                </Stack>

                {/* Session & Security */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Session &amp; Security</SectionTitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Session Timeout (hours)"
                                    type="number"
                                    fullWidth
                                    value={config.session_timeout_hours ?? ''}
                                    onChange={handleChange('session_timeout_hours')}
                                    helperText="Hours before investor session expires"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Email Verify Expiry (hours)"
                                    type="number"
                                    fullWidth
                                    value={config.email_verify_expiry_hours ?? ''}
                                    onChange={handleChange('email_verify_expiry_hours')}
                                    helperText="Hours before email verification token expires"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Max Concurrent Sessions"
                                    type="number"
                                    fullWidth
                                    value={config.max_concurrent_sessions ?? ''}
                                    onChange={handleChange('max_concurrent_sessions')}
                                    helperText="0 = unlimited"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Failed Login Lockout (minutes)"
                                    type="number"
                                    fullWidth
                                    value={config.failed_login_lockout_minutes ?? ''}
                                    onChange={handleChange('failed_login_lockout_minutes')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Max Failed Login Attempts"
                                    type="number"
                                    fullWidth
                                    value={config.max_failed_login_attempts ?? ''}
                                    onChange={handleChange('max_failed_login_attempts')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Max Failed TOTP Attempts"
                                    type="number"
                                    fullWidth
                                    value={config.max_failed_totp_attempts ?? ''}
                                    onChange={handleChange('max_failed_totp_attempts')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Deal Defaults */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Deal Defaults</SectionTitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Default Q&A Duration (hours)"
                                    type="number"
                                    fullWidth
                                    value={config.default_qa_duration_hours ?? ''}
                                    onChange={handleChange('default_qa_duration_hours')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Default Commitment Window (hours)"
                                    type="number"
                                    fullWidth
                                    value={config.default_commitment_window_hours ?? ''}
                                    onChange={handleChange('default_commitment_window_hours')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Deal Access Expiry (days)"
                                    type="number"
                                    fullWidth
                                    value={config.default_deal_access_expiry_days ?? ''}
                                    onChange={handleChange('default_deal_access_expiry_days')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Watermarking */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Watermarking</SectionTitle>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.watermark_enabled ?? true}
                                            onChange={handleChange('watermark_enabled')}
                                        />
                                    }
                                    label="Watermark Enabled"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    label="Watermark Text Template"
                                    fullWidth
                                    value={config.watermark_text_template ?? ''}
                                    onChange={handleChange('watermark_text_template')}
                                    helperText="Variables: {investor_name}, {date}"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <TextField
                                    label="Opacity"
                                    type="number"
                                    fullWidth
                                    inputProps={{ step: 0.05, min: 0, max: 1 }}
                                    value={config.watermark_opacity ?? ''}
                                    onChange={handleChange('watermark_opacity')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={config.convert_to_pdf_on_download ?? true}
                                            onChange={handleChange('convert_to_pdf_on_download')}
                                        />
                                    }
                                    label="Convert to PDF"
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Moderation */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Moderation</SectionTitle>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={config.qa_pre_moderation_enabled ?? true}
                                    onChange={handleChange('qa_pre_moderation_enabled')}
                                />
                            }
                            label="Q&A Pre-Moderation (require admin approval for investor questions)"
                        />
                    </CardContent>
                </Card>

                {/* Legal Text */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Legal Text</SectionTitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="NDA Version"
                                    fullWidth
                                    value={config.nda_version ?? ''}
                                    onChange={handleChange('nda_version')}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="NDA Text (HTML)"
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={config.nda_text ?? ''}
                                    onChange={handleChange('nda_text')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="ToS Version"
                                    fullWidth
                                    value={config.tos_version ?? ''}
                                    onChange={handleChange('tos_version')}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="Terms of Service Text (HTML)"
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={config.tos_text ?? ''}
                                    onChange={handleChange('tos_text')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Privacy Policy Version"
                                    fullWidth
                                    value={config.privacy_policy_version ?? ''}
                                    onChange={handleChange('privacy_policy_version')}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    label="Privacy Policy Text (HTML)"
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={config.privacy_policy_text ?? ''}
                                    onChange={handleChange('privacy_policy_text')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Branding */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <SectionTitle>Branding</SectionTitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Platform Name"
                                    fullWidth
                                    value={config.platform_name ?? ''}
                                    onChange={handleChange('platform_name')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Support Email"
                                    fullWidth
                                    type="email"
                                    value={config.support_email ?? ''}
                                    onChange={handleChange('support_email')}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Save button (bottom) */}
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<Icon icon={saveFill} />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Container>
        </Page>
    );
}
